import React from 'react';
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { theme } from '../../theme';
import { TableCell } from './Table';
import { useFocusVisible } from '../../hooks/useFocusVisible';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortableHeaderCellProps {
  /** Stable sort key reported back via onSort. */
  sortKey: string;
  /** Header label rendered to the user. */
  label?: React.ReactNode;
  /** Whether this column is sortable. Non-sortable columns render as a plain header. */
  sortable?: boolean;
  /** Currently-active sort key for the table. */
  activeKey?: string | null;
  /** Currently-active sort direction for the table. */
  activeDirection?: SortDirection;
  /** Called when the user clicks a sortable header. */
  onSort?: (key: string, direction: SortDirection) => void;
  /** Cycle order when clicking. Defaults to asc → desc → null. */
  cycle?: ('asc' | 'desc' | null)[];
  width?: string;
  align?: 'left' | 'center' | 'right';
  style?: React.CSSProperties;
}

/**
 * Shared sortable header cell. Single source of truth for the sort-arrow
 * look across DataGrid, AccountTable, and any other table that needs
 * column-header sorting. Click cycles asc → desc → null (clear).
 */
export const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  sortKey,
  label,
  sortable = true,
  activeKey,
  activeDirection,
  onSort,
  cycle = ['asc', 'desc', null],
  width,
  align,
  style,
}) => {
  const isActive = activeKey === sortKey;
  const { isFocusVisible, focusProps } = useFocusVisible();
  const interactive = sortable && !!onSort;

  const handleClick = () => {
    if (!sortable || !onSort) return;
    let next: SortDirection = cycle[0];
    if (isActive) {
      const idx = cycle.indexOf(activeDirection ?? null);
      next = cycle[(idx + 1) % cycle.length];
    }
    onSort(sortKey, next);
  };

  // A header that only sorts on click sorts for mice only. Enter and Space are
  // what a button responds to, and this is a button in everything but tag.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    e.preventDefault();
    handleClick();
  };

  return (
    <TableCell
      header
      width={width}
      align={align}
      aria-sort={
        interactive
          ? isActive && activeDirection === 'asc'
            ? 'ascending'
            : isActive && activeDirection === 'desc'
              ? 'descending'
              : 'none'
          : undefined
      }
      style={{
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none',
        position: 'relative',
        ...style,
      }}
    >
      <div
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...(interactive ? focusProps : {})}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive && typeof label === 'string' ? `Sort by ${label}` : undefined}
        style={{
          outline: isFocusVisible ? `2px solid ${theme.colors.brand.primary}` : 'none',
          outlineOffset: -2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
          gap: 6,
          padding: sortable ? '8px 4px' : '0',
          minHeight: sortable ? 36 : 'auto',
          borderRadius: 6,
          transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={(e) => {
          if (sortable) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
        }}
        onMouseLeave={(e) => {
          if (sortable) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {label !== undefined && <span style={{ fontWeight: isActive ? 700 : 600, whiteSpace: 'nowrap' }}>{label}</span>}
        {sortable && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}>
            {isActive && activeDirection === 'asc' ? (
              <ArrowUp size={16} style={{ color: theme.colors.brand.primary }} strokeWidth={2.5} />
            ) : isActive && activeDirection === 'desc' ? (
              <ArrowDown size={16} style={{ color: theme.colors.brand.primary }} strokeWidth={2.5} />
            ) : (
              <ChevronsUpDown size={16} style={{ color: theme.colors.text.tertiary, opacity: 0.5 }} strokeWidth={1.5} />
            )}
          </div>
        )}
      </div>
    </TableCell>
  );
};

export default SortableHeaderCell;
